// Production-quality per-policy content for SBC Ltd Legal pages.
// Format: use `**Heading**` for h2, `- ` prefix for bullet points, blank line for paragraph break.
// The Legal page renderer parses these tokens into styled elements.

const SIGNATURE = `
**Effective Date**
1 January 2026

**Version**
1.0

**Contact**
Scitygate Brewing Company Limited (Company No. 17323558, registered in England & Wales)
2nd Floor Halifax House, 93–101 Bridge Street
Manchester M3 2GX, United Kingdom
Email: help@sbcltd.com · Website: www.sbcltd.com
`;

const CONTENT = {
  "privacy-policy": `
Scitygate Brewing Company Ltd ("SBC", "we", "us", "our") is committed to protecting the personal information of every visitor, distributor, investor, employee and consumer who interacts with us. This Privacy Policy explains what personal information we collect, why we collect it, how we use it, and the rights available to you under the UK GDPR, the EU GDPR and other applicable data-protection laws.

**1. Who We Are**
Scitygate Brewing Company Limited (Company No. 17323558) is a company registered in England &amp; Wales with its headquarters at 2nd Floor Halifax House, 93–101 Bridge Street, Manchester M3 2GX, United Kingdom. We are the data controller for the personal information described in this policy.

**2. Information We Collect**
- Contact details you provide via forms (name, email, phone, company, country, delivery address).
- Distributor and investor application information (business details, licences, financial disclosures you elect to share).
- Career applications (CV, cover letter, work history, right-to-work confirmation).
- Website usage data (device type, browser, pages viewed, referring URL, timestamps, approximate location by IP).
- Cookies and similar technologies (see our Cookie Policy).

**3. How We Use Your Information**
- To respond to enquiries, sample requests and applications.
- To manage distributor, investor and supplier relationships.
- To process career applications.
- To operate, secure, improve and personalise our website.
- To communicate updates, news and marketing where you have opted in.
- To comply with legal, regulatory and audit obligations.

**4. Legal Bases**
We rely on: (a) consent — for optional marketing; (b) contract — to deliver services you request; (c) legitimate interests — to run our business responsibly; (d) legal obligation — to comply with law.

**5. Sharing & Transfers**
We do not sell your personal data. We share it with vetted service providers (hosting, email delivery, analytics, secure document storage, freight partners) under GDPR-compliant contracts. Where data is transferred outside the UK/EEA, we rely on adequacy decisions or Standard Contractual Clauses with supplementary safeguards.

**6. Retention**
We keep personal data only for as long as necessary for the purposes above, or as required by law. Distributor and finance records are retained for a minimum of six years for statutory compliance.

**7. Your Rights**
You have the right to access, rectify, erase, restrict, port and object to the processing of your personal data, and to lodge a complaint with the UK Information Commissioner's Office (ico.org.uk). To exercise any right, email help@sbcltd.com.

**8. Security**
We apply appropriate organisational and technical measures — encryption in transit, access controls, staff training, incident response — to protect your information.

**9. Updates**
We may update this Privacy Policy from time to time. Material changes will be highlighted on this page and, where appropriate, notified to you directly.
${SIGNATURE}`,

  "terms-and-conditions": `
These Terms & Conditions ("Terms") govern your access to and use of www.sbcltd.com and any related digital services operated by Scitygate Brewing Company Ltd ("SBC"). By using the website you accept these Terms in full.

**1. Use of the Website**
The website is provided for informational, commercial and recruitment purposes. You agree not to misuse it, including by attempting unauthorised access, scraping content at scale, or interfering with normal operation.

**2. Intellectual Property**
All content — text, imagery, product designs, trademarks, videos, downloads — is owned by SBC or licensed to SBC. "SBC", "Scitygate", "Maltora", "OneCola", "OneFresh", "VoltX" and "OneSpring" are registered or claimed trademarks of Scitygate Brewing Company Ltd. Nothing in these Terms transfers any IP rights to you.

**3. Product Information**
Product availability, specifications, ingredients and pricing vary by market and may change without notice. Legally binding offers are formed only through a signed contract or an accepted purchase order.

**4. Distributor & Investor Portals**
Access to portal features requires registration and acceptance of the applicable portal terms. You are responsible for keeping your credentials confidential.

**5. Third-Party Links**
The website may link to third-party sites. We are not responsible for their content or practices.

**6. Limitation of Liability**
To the fullest extent permitted by law, SBC excludes all liability for indirect, incidental or consequential loss arising from use of this website. Nothing in these Terms limits liability for death, personal injury caused by negligence, or fraud.

**7. Governing Law**
These Terms are governed by the laws of England & Wales. Any dispute is subject to the exclusive jurisdiction of the courts of England & Wales, without prejudice to consumer rights.
${SIGNATURE}`,

  "cookie-policy": `
We use cookies and similar technologies to make www.sbcltd.com work, improve its performance and understand how it is used.

**1. What is a Cookie**
A cookie is a small text file placed on your device. Cookies can be "session" (deleted when the browser closes) or "persistent" (retained until they expire or you delete them).

**2. Categories We Use**
- Strictly Necessary — required for the site to function (language selection, load balancing, security).
- Preferences — remember your language, currency and theme choices.
- Analytics — help us understand how visitors use the site so we can improve it (aggregate, non-identifying).
- Marketing — used only with your explicit consent, to deliver relevant campaigns and measure their performance.

**3. Managing Cookies**
You can accept or reject non-essential cookies via the cookie banner or your browser settings. Blocking strictly-necessary cookies may prevent parts of the website from functioning correctly.

**4. Third-Party Cookies**
Some cookies are set by third-party providers (for example, analytics or CDN partners). We ensure they operate under GDPR-compliant contracts.

**5. Updates**
This policy is reviewed at least annually and updated as our website evolves.
${SIGNATURE}`,

  "returns-refund-policy": `
This policy applies to product supplied directly by SBC Ltd to distributors, wholesalers and business customers. Retail consumer returns should be handled through the point of purchase.

**1. Eligibility**
Returns may be requested where product is (a) damaged in transit, (b) supplied non-conforming to the confirmed specification, or (c) shipped in error.

**2. Time Limits**
Notice of a return must be given in writing within 14 days of delivery, with supporting photographs, batch code and Bill of Lading reference.

**3. Inspection**
All returns are subject to inspection. Product that is damaged due to buyer negligence, incorrect storage or handling outside our specifications is not eligible.

**4. Resolution**
Approved returns are resolved by (a) credit note, (b) replacement in the next shipment, or (c) refund of the invoice value less freight and handling.

**5. Perishable & Regulated Product**
Perishable product returned outside its cold-chain or product for which regulatory clearance has been broken cannot be re-introduced to inventory and will be destroyed at buyer cost.
${SIGNATURE}`,

  "shipping-policy": `
SBC ships product globally through vetted freight partners under agreed Incoterms.

**1. Incoterms**
Default terms are FCA Manchester unless otherwise specified in the purchase order. FOB, CFR, CIF and DDP terms are available on request and subject to lane feasibility.

**2. Lead Times**
Standard lead time for a first order is 4–8 weeks from confirmed purchase order and receipt of the agreed deposit. Repeat orders are typically 2–4 weeks.

**3. Freight & Insurance**
We use ISO-audited freight forwarders and, on CIF orders, provide cargo insurance to 110% of invoice value against All-Risks.

**4. Documentation**
We provide Commercial Invoice, Packing List, Bill of Lading / Airway Bill, Certificate of Origin, Certificate of Analysis and additional certificates (Halal, Health, Free-Sale, Phytosanitary, Fumigation) on request.

**5. Delays**
Delays caused by force majeure, port congestion, customs inspections or destination authority action are outside our control and do not entitle the buyer to cancellation or damages.
${SIGNATURE}`,

  "accessibility-statement": `
SBC is committed to making www.sbcltd.com accessible to the widest possible audience, regardless of technology or ability.

**1. Standards**
We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA.

**2. Measures Taken**
- Semantic HTML structure and ARIA landmarks.
- Sufficient colour contrast in light and dark themes.
- Keyboard navigability across all interactive components.
- Alternative text for meaningful images.
- Resizable text without loss of content or function.
- Compatibility with modern screen readers (NVDA, JAWS, VoiceOver, TalkBack).

**3. Known Limitations**
Some third-party embedded content (e.g. legacy investor documents) may not fully meet our accessibility standard. We are working to remediate these areas.

**4. Feedback**
We welcome accessibility feedback. Please contact help@sbcltd.com and describe the barrier, the page URL and your assistive technology if known. We aim to respond within 5 business days.
${SIGNATURE}`,

  "modern-slavery-statement": `
SBC has zero tolerance for slavery, servitude, forced labour and human trafficking in our operations or supply chain. This statement is published pursuant to Section 54 of the UK Modern Slavery Act 2015.

**1. Our Business**
SBC produces and distributes non-alcoholic beverages under the Maltora, OneCola, OneFresh, VoltX and OneSpring brands. Our supply chain covers ingredients, packaging, contract manufacturing, freight and marketing services across the UK, Europe, Africa and the Middle East.

**2. Policies & Governance**
- Modern Slavery, Whistleblowing and Ethics policies apply to every employee and contractor.
- Suppliers must accept our Supplier Code of Conduct.
- The Board reviews modern-slavery risk annually.

**3. Due Diligence**
- Risk-tiered supplier assessment covering geography, sector and workforce type.
- Written self-declarations for all Tier 1 suppliers.
- Onsite audits for high-risk categories.
- Grievance channel operated independently.

**4. Training**
All employees complete mandatory awareness training on modern slavery indicators and reporting routes.

**5. Reporting Concerns**
Anyone with concerns can email help@sbcltd.com or use our confidential whistleblowing line. Retaliation against those who report in good faith is strictly prohibited.
${SIGNATURE}`,

  "sustainability-policy": `
Sustainability is central to SBC's strategy. Our goal is to deliver world-class beverages while reducing environmental impact and creating shared value for the communities we serve.

**1. Focus Areas**
- Packaging — recyclable materials, lightweighting, and closed-loop PET by 2027.
- Energy — rooftop solar, energy-efficient utilities and renewable-electricity contracts.
- Water — water-positive by 2030 in every plant we operate.
- Community — local sourcing, jobs, skills training and social investment.
- Responsible sourcing — traceable ingredients aligned to our Supplier Code.

**2. Governance**
The Executive Committee owns sustainability performance. Objectives cascade into functional KPIs and are reported annually.

**3. Reporting**
We publish an annual Sustainability Report aligned to widely recognised frameworks (GRI, SASB, TCFD as applicable).

**4. Partnerships**
We collaborate with recyclers, NGOs and industry associations to accelerate impact beyond our own operations.
${SIGNATURE}`,

  "quality-policy": `
SBC's promise is world-class quality in every bottle, can and pack. This policy commits SBC and every supplier acting on its behalf to the highest standards of product quality.

**1. Principles**
- Design quality into every product from formulation to shelf.
- Rigorously verify quality at incoming, in-process and outgoing stages.
- Never release non-conforming product to market.
- Continuously improve through data, root-cause analysis and training.

**2. Systems**
We operate ISO 9001-aligned quality management with documented procedures, calibrated equipment, batch traceability and structured supplier management.

**3. Responsibilities**
Quality is everyone's responsibility. Functional Quality leaders have unqualified authority to hold or release product.

**4. Consumer Feedback**
All consumer feedback is investigated. Systemic issues trigger corrective and preventive action.
${SIGNATURE}`,

  "food-safety-policy": `
SBC is committed to producing safe, wholesome beverages that consumers can trust.

**1. Standards**
Our food-safety management system is built on HACCP principles and aligns to FSSC 22000 and ISO 22000.

**2. Controls**
- Approved-supplier programme for all ingredients and packaging.
- Environmental monitoring across production areas.
- CCP validation and verification with documented records.
- Batch release only after chemical, microbiological and sensory approval.
- Full end-to-end traceability from raw material lot to finished pack.

**3. Recall Capability**
Documented, rehearsed mock-recall procedures ensure we can withdraw affected product from any market within 4 hours of a confirmed risk.

**4. Regulatory Compliance**
We comply with UK, EU and destination-market food legislation, labelling, allergen and additive regulations.
${SIGNATURE}`,

  "export-terms": `
These Export Terms govern the sale and shipment of SBC product to buyers outside the United Kingdom.

**1. Incoterms**
Default: FCA Manchester. Alternatives (FOB, CFR, CIF, DDP) available by agreement.

**2. Pricing & Currency**
All prices are quoted in GBP unless otherwise agreed. Pricing is valid for 30 days from quotation.

**3. Payment**
Standard payment terms: 30% advance deposit and 70% against copy Bill of Lading, by irrevocable telegraphic transfer to our nominated GBP account. Letter of Credit acceptable for orders above USD 250,000.

**4. Import Compliance**
The buyer is responsible for import licences, product registration, duties, taxes and destination-market compliance. SBC will supply reasonable documentation to support registration.

**5. Force Majeure**
Neither party is liable for delay or non-performance caused by events beyond reasonable control (war, epidemic, port closure, government action, natural disaster).

**6. Governing Law**
These terms are governed by the laws of England & Wales; disputes are subject to the exclusive jurisdiction of the courts of England & Wales, without prejudice to any mandatory local law.
${SIGNATURE}`,

  "distributor-terms": `
Appointment as an SBC distributor is subject to written agreement. These general terms outline the framework applicable to every appointment.

**1. Appointment**
SBC appoints distributors on a market-specific basis, non-exclusive by default. Exclusivity may be granted subject to minimum-volume commitments.

**2. Obligations**
Distributors will (a) promote SBC brands in accordance with our brand guidelines, (b) maintain suitable storage and cold-chain where required, (c) achieve agreed minimum volumes, (d) respect all applicable law, and (e) not modify, re-label or repackage product.

**3. Pricing**
Wholesale price lists are confidential and subject to revision on 30 days' notice.

**4. Minimum Order**
MOQ is 1 x 20ft FCL container per SKU family. Mixed containers accepted by agreement.

**5. Brand & IP**
Distributors are granted a limited, revocable, non-exclusive licence to use SBC trademarks solely to promote and sell SBC product within the appointed territory.

**6. Termination**
Either party may terminate on 90 days' notice. SBC may terminate immediately for material breach, insolvency or reputational risk.
${SIGNATURE}`,

  "website-disclaimer": `
Information on this website is provided in good faith for general information purposes only.

**1. No Warranty**
While we make every effort to ensure accuracy, SBC makes no warranty (express or implied) as to the completeness, accuracy or reliability of any information on this website.

**2. Product Information**
Product availability, specifications, ingredient declarations and pricing vary by market and may change without notice. Always refer to the physical pack label for the definitive product information.

**3. Third-Party Content**
Any references to third-party organisations, websites or products are for information only and do not imply endorsement.

**4. No Advice**
Nothing on this website constitutes medical, dietary, legal or financial advice. Consult a qualified professional where appropriate.

**5. Limitation of Liability**
SBC accepts no liability for loss or damage arising from use of, or reliance on, information on this website, to the fullest extent permitted by law.
${SIGNATURE}`,

  "intellectual-property": `
All intellectual property associated with SBC and its brands is protected by law.

**1. Trademarks**
"SBC", "Scitygate", "Scitygate Brewing", "Maltora", "OneCola", "OneFresh", "VoltX", "OneSpring" and associated logos, taglines and trade dress are registered or claimed trademarks of Scitygate Brewing Company Ltd. Use without written permission is prohibited.

**2. Copyright**
All text, imagery, video, audio, product designs and software on this website are © Scitygate Brewing Company Ltd or its licensors and are protected by UK, EU and international copyright law.

**3. Permitted Use**
You may view, download and print material for personal or business reference. Any other use — including reproduction, republication or commercial exploitation — requires our written permission.

**4. Distributor & Media Assets**
Approved distributors and media partners may access limited-use brand assets under the terms of their access agreement.

**5. Infringement**
We actively enforce our intellectual property rights. To report suspected infringement, contact help@sbcltd.com.
${SIGNATURE}`,

  "tax-policy": `
Scitygate Brewing Company Limited is committed to fully and transparently complying with all tax laws that apply to our business — in the United Kingdom and in every jurisdiction in which we operate. This policy sets out the principles under which the company approaches taxation.

**1. UK Tax Compliance**
SBC complies with all tax legislation issued by the UK Parliament and administered by His Majesty's Revenue & Customs (HMRC). This includes Corporation Tax, VAT, PAYE / National Insurance, business rates and any excise duties applicable to non-alcoholic beverage manufacture, import and export.

**2. Responsible Taxation**
We do not use aggressive tax planning, artificial structures or arrangements that lack commercial substance. All transactions between SBC and any related party are conducted at arm's length and in accordance with UK transfer-pricing rules.

**3. Transparency & Reporting**
Our statutory accounts are prepared under UK GAAP (FRS 102) and filed on time with Companies House. Tax returns are submitted accurately and within the deadlines set by HMRC. Where a country-by-country report becomes applicable to us in the future, we will publish it in line with OECD BEPS Action 13.

**4. Anti-Tax-Evasion Statement**
SBC has zero tolerance for the facilitation of tax evasion — whether by our employees, contractors, distributors, suppliers or any associated person — as prohibited by the Criminal Finances Act 2017. We maintain reasonable prevention procedures, provide staff training, and require all business partners to acknowledge our anti-facilitation stance.

**5. Governance & Oversight**
Ultimate responsibility for this policy rests with the Board of Directors. The Finance Director is responsible for day-to-day tax risk management. Material tax positions are reviewed at least annually and disclosed to our external auditors.

**6. Cooperation with HMRC**
We maintain a transparent and constructive relationship with HMRC. We seek clearance in advance of significant or novel transactions where uncertainty exists, and we respond fully and promptly to any HMRC enquiry.

**7. Reporting Concerns**
Any concern relating to tax integrity can be raised confidentially by emailing help@sbcltd.com. All reports are handled sensitively and without retaliation.
${SIGNATURE}`,

  "gender-pay-gap-report": `
Scitygate Brewing Company Limited is committed to equal opportunities, fair pay and an inclusive workplace where every colleague has the opportunity to progress on merit.

**Current Position**
SBC Ltd is committed to equal opportunities, fair pay and workplace inclusion. As the company grows, Gender Pay Gap Reports will be published in accordance with applicable UK legislation where reporting requirements apply.

**Why We Are Not Yet Reporting Statistics**
Under the UK Equality Act 2010 (Gender Pay Gap Information) Regulations 2017, statutory reporting is required only for employers with a headcount of 250 or more employees on a snapshot date. Publishing partial or unverified figures below that threshold would not add clarity and could mislead readers, which is why we have chosen not to publish statistics that have not been independently verified.

**Our Commitments**
- Every role at SBC is graded and remunerated on the basis of responsibilities, skills, experience and performance — never gender.
- Recruitment, promotion, training and reward decisions follow structured, documented processes.
- We publish gender-diverse shortlists for every senior appointment.
- All new managers complete inclusive-leadership training.
- We conduct an annual internal pay-equity review across the entire workforce, regardless of statutory obligation.
- We track applications, offers, promotions and leavers by gender to identify and remove any structural bias.

**Governance**
Progress on equality, diversity and inclusion (EDI) is owned by the Executive Committee and reviewed by the Board of Directors at least annually.

**Future Reporting**
When SBC Ltd reaches the 250-employee statutory threshold — or where any voluntary framework we join (for example UN Women's Empowerment Principles) requires public disclosure — we will publish a full Gender Pay Gap Report on this page, using the official snapshot date and calculation methodology mandated by the Regulations.

**Reporting Concerns**
Any colleague, candidate or observer who believes a decision at SBC has not been made on the basis of merit can raise a confidential concern by emailing help@sbcltd.com. All reports are investigated sensitively and without retaliation.
${SIGNATURE}`,
};

// Extra pages kept in the sidebar keep the previous concise wording; they're
// still valuable but not part of the user's P2 spotlight list.
const FALLBACK = (title) => `
This policy sets out how Scitygate Brewing Company Ltd approaches ${title.toLowerCase()}. It applies to the company, its subsidiaries and every employee, contractor and business partner acting on its behalf.

**1. Scope**
This policy governs all activities relating to ${title.toLowerCase()} across SBC Ltd's UK operations, our planned export markets, and any territory in which SBC brands are sold, distributed or represented.

**2. Principles**
- Compliance with applicable UK, EU and international law.
- Transparent, ethical and accountable business conduct.
- Continuous improvement through review and measurable objectives.
- Fair, respectful treatment of all stakeholders.
- Protection of consumer trust and brand integrity.

**3. Responsibilities**
The Board of Directors is ultimately responsible for this policy. Day-to-day implementation is delegated to functional leaders and reviewed at least annually.

**4. Monitoring & Review**
We review this policy annually and whenever there is a material change in law, business activity or stakeholder expectation.

**5. Reporting Concerns**
Any concerns relating to this policy can be raised confidentially by emailing help@sbcltd.com. All reports are handled sensitively and without retaliation.
${SIGNATURE}`;

export function getLegalBody(slug, title) {
  return (CONTENT[slug] || FALLBACK(title)).trim();
}
